import { Breadcrumb, BreadcrumbItem } from "flowbite-react";
import { HiHome, HiFilter } from "react-icons/hi";
import { useEffect, useRef, useState } from "react";
import Loader from "../../Components/Loader/Loader";

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getCookie } from "../../reducer/utils";
import LiveOTTEventFilter from "../../Components/SideFilters/LiveOTTEventFilter";
import { Chart } from "react-google-charts";
import { TablePagination } from "../../Components/Pagination/Pagination";


function LiveOTTEvent() {

  const baseURL = process.env.REACT_APP_BASE_URL;

  const toastId = useRef(null);

  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [fetchingEvents, setFetchingEvents] = useState(false);
  const [events, setEvents] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 50;
  const [pagination, setPagination] = useState({
    "start": 0,
    "page_size": pageSize
  });

  const [filter, setFilter] = useState({});
  const [pageNo, setPageNo] = useState(0);
  const [view, setView] = useState("table");

  useEffect(() => {
    setPagination({
      "start": pageNo * pageSize,
      "page_size": pageSize
    });
  }, [pageNo]);

  const onApplyFilter = (filter_payload) => {
    setPagination({...pagination, start: 0});
    const localFilter = {}
    console.log("filter_payload", filter_payload);

    if (filter_payload.eventTime !== undefined) {
      localFilter.event_time = {};  // Initialize event_time object first
      if (filter_payload.eventTime.gte) {
        localFilter.event_time.gte = filter_payload.eventTime.gte;
      }
      if (filter_payload.eventTime.lte) {
        localFilter.event_time.lte = filter_payload.eventTime.lte;
      }
    }

    if (filter_payload.eventTitle !== undefined) {
      localFilter.event_title = {
        like: filter_payload.eventTitle
      };
    }

    if (filter_payload.providerFilter !== undefined) {
      const providerNames = Object.keys(filter_payload.providerFilter).filter(key => filter_payload.providerFilter[key]);
      if (providerNames.length > 0) {
        localFilter.provider_name = {
          in_list: providerNames
        };
      }
    }

    if (filter_payload.subscription !== null) {
      localFilter.is_subscribed = filter_payload.subscription;
    }

    setFilter(localFilter);
    setShowCategoryFilter(false);
  }
  
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: '2-digit',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  }

  const getEvents = async () => {
    toastId.current = toast.loading('Fetching events...');

    const response = await fetch(`${baseURL}/api/v4/live-ott/events/filter`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${getCookie("token")}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...filter,
        pagination_metadata: pagination
      })
    });
    if (!response.ok) {
      // Reset to safe defaults on error (e.g. 403) so the effect doesn't crash.
      setEvents([]);
      setHasMore(false);
      toast.update(toastId.current, {
        render:
          response.status === 401 || response.status === 403
            ? 'Session expired. Please log in again.'
            : 'Failed to fetch events',
        type: 'error',
        isLoading: false,
        autoClose: 2000,
      });
      return;
    }

    const data = await response.json();
    setEvents(data?.['events'] ?? []);
    setHasMore(data?.['pagination_metadata']?.['has_more'] ?? false);
    toast.update(toastId.current, {
      render: 'Events fetched successfully',
      type: 'success',
      isLoading: false,
      autoClose: 1000,
    });
  }


  useEffect(() => {
    getEvents();
  }, [filter, pageNo]);


  // First row defines the columns
  const columns = [
    { type: "string", label: "Task ID" },
    { type: "string", label: "Task Name" },
    { type: "string", label: "Resource" },
    { type: "date", label: "Start Date" },
    { type: "date", label: "End Date" },
    { type: "number", label: "Duration" },
    { type: "number", label: "Percent Complete" },
    { type: "string", label: "Dependencies" },
  ];


  const defaultOptions = {
    height: 400,
    gantt: {
      trackHeight: 30,
    },
  };

  const [options, setOptions] = useState(defaultOptions);

  const [data, setData] = useState([]);

  const calcPercentComplete = (event) => {
    const end_time = new Date(event.event_end_time || new Date(event.event_time).getTime() + (6 * 60 * 60 * 1000));
    const start_time = new Date(event.event_time);
    const duration = end_time - start_time;
    const percentComplete = Math.min((new Date() - start_time) / duration * 100, 100);
    return percentComplete;
  }

  useEffect(() => {

    if(!events || events.length === 0) return;

    const rows = events.map(event => [
      event.dt_known_id,
      event.event_title,
      event.dt_known_id,
      new Date(event.event_time),
      new Date(event.event_end_time || new Date(event.event_time).getTime() + (6 * 60 * 60 * 1000)),
      null,
      calcPercentComplete(event),
      null,
    ]);

    setData([columns, ...rows]);
    setOptions({...options, height: rows.length * 30+50});
  }, [events]);


  return (
    <>
      <ToastContainer />

      <LiveOTTEventFilter showCategoryFilter={showCategoryFilter} setShowCategoryFilter={setShowCategoryFilter} onApplyFilter={onApplyFilter} filter={filter} />

      {
        fetchingEvents && <Loader />
      }

      <div className="bg-white px-4 py-4 rounded-lg border-solid border-2 border-grey1 flex items-center justify-between">
        <Breadcrumb aria-label="Default breadcrumb example">  
          <BreadcrumbItem href="/dashboard" icon={HiHome} />
          <BreadcrumbItem className='text-primary1'>Home</BreadcrumbItem>
        </Breadcrumb>
      </div>

      <div className="p-6 bg-white rounded-lg shadow-md mt-4">

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Live OTT Events</h2>
          <div className="flex space-x-2">

            <button className="flex items-center border rounded-md px-4 py-2 gap-2" onClick={() => setShowCategoryFilter(true)}>
              <HiFilter />
              <span className="mr-2">Filter</span>
            </button>

          </div>
        </div>

        <div className="flex items-center mb-4">  
          <button className={`flex items-center border rounded-s-md px-4 py-2 gap-2 ${view=="table" ? 'bg-gray-300': ""}`} onClick={() => setView("table")}> Table </button>
          <button className={`flex items-center border rounded-e-md px-4 py-2 gap-2 ${view=="gantt" ? 'bg-gray-300': ""}`} onClick={() => setView("gantt")}> Gantt </button>
        </div>
        <div className={view === "gantt" ? "" : "hidden"}>

        
          {
            data.length > 0 ? <Chart
              chartType="Gantt"
              width="100%"
              height="100%"
              data={data}
              options={options}
            /> : <div className="flex justify-center items-center h-full">
              <p className="text-gray-500">Loading...</p>
            </div>
          }
        </div>


        <div className={`overflow-x-auto border rounded-2xl ${view === "table" ? "" : "hidden"}`}>
          <table className="min-w-full whitespace-nowrap">
            <thead className="bg-newgrey">
              <tr>
                <th className="px-4 py-4 text-left text-secondary1">Dt Known Id</th>
                <th className="px-4 py-4 text-left text-secondary1">Title</th>
                <th className="px-4 py-4 text-left text-secondary1">Start Time</th>
                <th className="px-4 py-4 text-left text-secondary1">End Time</th>
                <th className="px-4 py-4 text-left text-secondary1">Status</th>
                <th className="px-4 py-4 text-left text-secondary1">Classification</th>
                <th className="px-4 py-4 text-left text-secondary1">Categories</th>
                <th className="px-4 py-4 text-left text-secondary1">Subscribed</th>
                <th className="px-4 py-4 text-left text-secondary1">Description</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {
                events?.map((value, index) => (
                  <tr className={`border-b ${index === 0 ? 'border-t' : ''}`} key={value.id}>
                    <td className="px-4 py-4 text-black font-bold">{value.dt_known_id}</td>
                    <td className="px-4 py-4 text-black font-bold">
                      <a href={value.event_link} target="_blank" className="text-blue">
                        {value.event_title}
                      </a>
                    </td>
                    
                    <td className="px-4 py-4 text-black font-bold">{formatDate(value.event_time)}</td>
                    <td className="px-4 py-4 text-black font-bold">{formatDate(value.event_end_time)}</td>
                    <td className="px-4 py-4 text-black font-bold">{value.status}</td>
                    <td className="px-4 py-4 text-black font-bold">{value.classification ?? '--'}</td>


                    {
                      value.category_names?.some(category => category.is_subscribed) 
                        ? 
                        <td className="px-4 py-4 text-green-500 font-bold">
                          Subscribed
                        </td>
                        : 
                        <td className="px-4 py-4 text-secondary1 font-bold">
                          Not Subscribed
                    </td>
                    }
                    
                    <td className="px-4 py-4 text-black font-bold">
                      {value.category_names?.map((category, i) => (
                        <span key={i}>
                          {i > 0 && ', '}
                          {category['category_name']} ({category['provider_name']})
                        </span>
                      ))}
                    </td>
                    <td className="px-4 py-4 text-black font-bold">{value.event_description?.length > 50 ? value.event_description.substring(0, 50) + '...' : value.event_description}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
          <TablePagination setPageNo={setPageNo} page_size={pageSize} start={pagination.start} has_more={hasMore} />
      </div>

    </>
  )
}

export default LiveOTTEvent